import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiHome, FiUsers, FiMessageCircle, FiDatabase, FiCalendar, FiPieChart, FiZap, FiTrendingUp, FiShield, FiSettings, FiLogOut, FiChevronDown, FiMenu, FiX } from 'react-icons/fi';
import NotificationBell from '../NotificationBell';
import '../../styles/topnav.css';

const mainNavItems = [
  { path: '/admin/dashboard', label: 'Command Center', icon: <FiHome /> },
  { path: '/admin/users', label: 'Squad', icon: <FiUsers /> },
  { path: '/admin/campaigns', label: 'Outreach', icon: <FiMessageCircle /> },
  { path: '/admin/master-data', label: 'Arsenal', icon: <FiDatabase /> },
  { path: '/admin/schedules', label: 'Planner', icon: <FiCalendar /> },
];

const analyticsItems = [
  { path: '/admin/reports', label: 'Intelligence', icon: <FiPieChart /> },
  { path: '/admin/insights', label: 'Radar', icon: <FiZap /> },
  { path: '/admin/performance', label: 'Metrics', icon: <FiTrendingUp /> },
];

const settingsItems = [
  { path: '/admin/audit-logs', label: 'Activity Log', icon: <FiShield /> },
  { path: '/admin/settings', label: 'Config', icon: <FiSettings /> },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const isAnalyticsActive = analyticsItems.some(item => location.pathname === item.path);
  const isSettingsActive = settingsItems.some(item => location.pathname === item.path);

  return (
    <div className="app-layout topnav-layout">
      <nav className="topnav">
        {/* Brand */}
        <div className="topnav-brand" onClick={() => navigate('/admin/dashboard')}>
          <img src="/Conquerlogo.png" alt="Logo" className="topnav-logo" />
          <div className="topnav-brand-text">
            <h1>Conquer</h1>
            <span>ADMIN PANEL</span>
          </div>
        </div>

        {/* Mobile Toggle */}
        <button className="topnav-mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <FiX /> : <FiMenu />}
        </button>

        {/* Navigation Menu */}
        <ul className={`topnav-menu ${mobileMenuOpen ? 'open' : ''}`}>
          {mainNavItems.map(item => (
            <li key={item.path} className="topnav-item">
              <NavLink to={item.path} className={({ isActive }) => `topnav-link ${isActive ? 'active' : ''}`}>
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}

          {/* Analytics Dropdown */}
          <li className="topnav-item topnav-dropdown">
            <button className={`topnav-dropdown-toggle ${isAnalyticsActive ? 'active' : ''}`}>
              <FiPieChart />
              <span>Analytics</span>
              <FiChevronDown className="chevron" />
            </button>
            <div className="topnav-dropdown-menu">
              {analyticsItems.map(item => (
                <NavLink key={item.path} to={item.path} className={({ isActive }) => `topnav-dropdown-item ${isActive ? 'active' : ''}`}>
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </li>

          {/* Settings Dropdown */}
          <li className="topnav-item topnav-dropdown">
            <button className={`topnav-dropdown-toggle ${isSettingsActive ? 'active' : ''}`}>
              <FiSettings />
              <span>More</span>
              <FiChevronDown className="chevron" />
            </button>
            <div className="topnav-dropdown-menu">
              {settingsItems.map(item => (
                <NavLink key={item.path} to={item.path} className={({ isActive }) => `topnav-dropdown-item ${isActive ? 'active' : ''}`}>
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </li>
        </ul>

        {/* Right Section */}
        <div className="topnav-right">
          <NotificationBell role="admin" />
          <div className="topnav-user" onClick={() => navigate('/admin/settings')}>
            <div className="topnav-avatar">{user?.username?.[0]?.toUpperCase()}</div>
            <div className="topnav-user-info">
              <span>{user?.username}</span>
              <small>{user?.role}</small>
            </div>
          </div>
          <button className="topnav-logout" onClick={handleLogout} title="Logout">
            <FiLogOut />
          </button>
        </div>
      </nav>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
